import { useCallback, useEffect, useState } from "react";
import {
  getNotificationPermission,
  isBrowserNotificationSupported,
} from "@/utils/browser-notification-utils";

/**
 * Reactive OS notification permission. Seeds from `Notification.permission` and
 * stays in sync via the Permissions API, so the UI updates live when the user
 * changes the site permission in their browser settings (no page reload needed).
 * Also re-reads on window focus / tab visibility as a fallback, and exposes
 * `refresh()` for an immediate re-read (e.g. right after requesting permission).
 */
export function useNotificationPermission() {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    getNotificationPermission()
  );

  const refresh = useCallback(
    () => setPermission(getNotificationPermission()),
    []
  );

  useEffect(() => {
    if (!isBrowserNotificationSupported()) return;

    refresh();

    let status: PermissionStatus | undefined;
    let cancelled = false;

    // Permissions API: fires when the browser-level grant changes (allow/block/
    // reset), letting the toggle re-enable without a reload. Not all browsers
    // expose the "notifications" permission here — fall back to focus/visibility.
    navigator.permissions
      ?.query({ name: "notifications" as PermissionName })
      .then((permissionStatus) => {
        if (cancelled) return;
        status = permissionStatus;
        status.onchange = refresh;
      })
      .catch(() => {
        // Permissions API unavailable for "notifications" — fallbacks cover us.
      });

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refresh]);

  return { permission, refresh };
}
